import { useCallback, useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Flame, Users, Sparkles, Trophy, Download, MessageSquare, Plus, Clock, Search, AlarmClock, Paperclip, FileText, BadgePercent, Send, Upload, CalendarDays, Link2, Star, MessageCircle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { OffersPanel } from "@/components/OffersPanel";
import { OfferHistory } from "@/components/OfferHistory";
import { ReferralPost } from "@/components/ReferralPost";
import { ReferralsPanel } from "@/components/ReferralsPanel";
import { ReviewsPanel } from "@/components/ReviewsPanel";
import { ConversionChart } from "@/components/ConversionChart";
import { TopSimulations } from "@/components/TopSimulations";
import { WinRates } from "@/components/WinRates";
import { RevenueTracker } from "@/components/RevenueTracker";
import { Appointments } from "@/components/Appointments";
import { PendingReviews } from "@/components/PendingReviews";
import { ReviewShare } from "@/components/ReviewShare";
import { ReviewQRCard } from "@/components/ReviewQRCard";
import { BusinessCard } from "@/components/BusinessCard";
import { GoogleRating } from "@/components/GoogleRating";
import { TrafficPanel } from "@/components/TrafficPanel";
import { PulsePanel } from "@/components/PulsePanel";
import { RegionStats } from "@/components/RegionStats";
import { ApolloPanel } from "@/components/ApolloPanel";
import { BlogPanel } from "@/components/BlogPanel";
import { BrusselsProspector } from "@/components/BrusselsProspector";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

const scoreStyle = {
  hot: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  warm: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  cold: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
  pending: "bg-white/5 text-white/40 border-white/10",
};

const statusStyle = {
  new: "text-[#FF7900]",
  contacted: "text-sky-400",
  converted: "text-emerald-400",
  lost: "text-zinc-500",
};

const waitStyle = (days) =>
  days >= 4
    ? "border-red-500/40 bg-red-500/10 text-red-400"
    : days >= 2
    ? "border-amber-500/40 bg-amber-500/10 text-amber-400"
    : "border-white/10 text-white/40";

const waitDays = (createdAt) => Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000);

const todayStr = () => new Date().toISOString().slice(0, 10);
const isSnoozed = (l) => l.snoozed_until && l.snoozed_until > todayStr();
const snoozeExpired = (l) => l.snoozed_until && l.snoozed_until <= todayStr();

export default function Dashboard() {
  const { t } = useLang();
  const d = t.dash;
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("leads");
  const [oldestFirst, setOldestFirst] = useState(false);
  const [search, setSearch] = useState("");
  const [openNotes, setOpenNotes] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [openSnooze, setOpenSnooze] = useState(null);
  const [snoozeDate, setSnoozeDate] = useState("");

  const setSnooze = async (id, until) => {
    await api.patch(`/leads/${id}/snooze`, { until });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, snoozed_until: until ? until.slice(0, 10) : null } : l)));
    setOpenSnooze(null);
    setSnoozeDate("");
  };

  const saveValue = async (id, v) => {
    const num = parseFloat(v);
    if (isNaN(num) || num < 0) return;
    await api.patch(`/leads/${id}/value`, { monthly_value: num });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, monthly_value: num } : l)));
  };

  const addNote = async (id) => {
    if (!noteText.trim()) return;
    const res = await api.post(`/leads/${id}/notes`, { text: noteText });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, notes: [...(l.notes || []), res.data] } : l)));
    setNoteText("");
  };

  const removeNote = async (leadId, noteId) => {
    await api.delete(`/leads/${leadId}/notes/${noteId}`);
    setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, notes: (l.notes || []).filter((n) => n.id !== noteId) } : l)));
  };

  const [analyzing, setAnalyzing] = useState(null);
  const [sendingProposal, setSendingProposal] = useState(null);

  const sendProposal = async (l) => {
    setSendingProposal(l.id);
    try {
      const res = await api.post(`/leads/${l.id}/send-proposal`);
      setLeads((ls) => ls.map((x) => (x.id === l.id ? { ...x, proposal_sent_at: res.data.proposal_sent_at } : x)));
      toast.success(`${d.proposalSent} ${res.data.to}`);
    } catch {
      toast.error(d.proposalFailed);
    } finally {
      setSendingProposal(null);
    }
  };

  const analyzeBill = async (id) => {
    setAnalyzing(id);
    try {
      const res = await api.post(`/leads/${id}/analyze-bill`);
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, ...res.data } : l)));
    } catch {
      toast.error(d.billAI.failed);
    } finally {
      setAnalyzing(null);
    }
  };

  const downloadBrief = async (l) => {
    const res = await api.get(`/leads/${l.id}/brief`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lead-${l.name.replace(/\s+/g, "-")}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadProposal = async (l) => {
    const res = await api.get(`/leads/${l.id}/proposal`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `proposition-${l.name.replace(/\s+/g, "-")}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openBill = async (l) => {
    try {
      const res = await api.get(`/bills/${l.bill_path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      // ignore
    }
  };

  const copyPortal = async (l) => {
    await navigator.clipboard.writeText(`${window.location.origin}/client/${l.id}`);
    toast.success(d.portalCopied);
  };

  const copyReviewLink = async (l) => {
    const first = (l.name || "").trim().split(" ")[0];
    await navigator.clipboard.writeText(`${window.location.origin}/avis${first ? `?nom=${encodeURIComponent(first)}` : ""}`);
    toast.success(d.reviewLinkCopied);
  };

  const copyReviewSms = async (l) => {
    const first = (l.name || "").trim().split(" ")[0];
    const link = `${window.location.origin}/avis${first ? `?nom=${encodeURIComponent(first)}` : ""}`;
    const msg = l.lang === "en"
      ? `Hello ${first || ""}! Thank you for your trust 🙏 A short review would help me a lot: ${link}`
      : `Bonjour ${first || ""} ! Merci pour votre confiance 🙏 Un petit avis m'aiderait énormément : ${link}`;
    await navigator.clipboard.writeText(msg.replace("  ", " "));
    toast.success(d.reviewSmsCopied);
  };

  const saveDetail = async (id, key, value) => {
    try {
      await api.patch(`/leads/${id}/details`, { [key]: value });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, [key]: value || null } : l)));
    } catch {
      toast.error(d.details.failed);
    }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/leads/import", fd);
      toast.success(`${res.data.imported} ${d.importDone} (${res.data.duplicates} ${d.importDupes})`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || d.importFailed);
    }
  };

  const exportCsv = async () => {
    const res = await api.get("/leads/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "smart-telecom-advisor-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const load = useCallback(async () => {
    try {
      const [leadsRes, statsRes] = await Promise.all([api.get("/leads"), api.get("/stats")]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("smartsn_token");
        navigate("/login");
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("smartsn_token")) {
      navigate("/login");
      return;
    }
    load();
  }, [load, navigate]);

  const updateStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
  };

  const remove = async (id) => {
    await api.delete(`/leads/${id}`);
    setLeads((ls) => ls.filter((l) => l.id !== id));
  };

  const logout = () => {
    localStorage.removeItem("smartsn_token");
    navigate("/login");
  };

  const sortedLeads =
    leads &&
    (search.trim()
      ? leads.filter((l) => {
          const s = search.toLowerCase();
          return [l.name, l.phone, l.email, l.city, l.referred_by].some((v) => (v || "").toLowerCase().includes(s));
        })
      : leads
    ).slice().sort((a, b) => (oldestFirst ? new Date(a.created_at) - new Date(b.created_at) : new Date(b.created_at) - new Date(a.created_at)));

  const statCards = stats
    ? [
        { label: d.stats.total, value: stats.total, icon: Users },
        { label: d.stats.fresh, value: stats.new, icon: Sparkles },
        { label: d.stats.hot, value: stats.hot, icon: Flame },
        { label: d.stats.converted, value: stats.converted, icon: Trophy },
      ]
    : [];

  return (
    <main className="min-h-screen" data-testid="dashboard-page">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-2xl">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={32} />
            <div>
              <p className="font-display font-semibold leading-none">{d.title}</p>
              <p className="text-xs text-white/40">{d.subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label
              data-testid="dashboard-import-button"
              title={d.importHint}
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FF7900] hover:text-[#FF7900] text-sm px-4 py-2 rounded-full transition-colors duration-300 cursor-pointer"
            >
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">{d.importCsv}</span>
              <input type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="dashboard-import-input" />
            </label>
            <button
              onClick={exportCsv}
              data-testid="dashboard-export-button"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FF7900] hover:text-[#FF7900] text-sm px-4 py-2 rounded-full transition-colors duration-300"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">{d.export}</span>
            </button>
            <button
              onClick={load}
              data-testid="dashboard-refresh-button"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FF7900] hover:text-[#FF7900] text-sm px-4 py-2 rounded-full transition-colors duration-300"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">{d.refresh}</span>
            </button>
            <button
              onClick={logout}
              data-testid="dashboard-logout-button"
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-[#FF7900] text-sm px-4 py-2 rounded-full transition-colors duration-300"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{d.logout}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10"
          data-testid="dashboard-stats"
        >
          {statCards.map((s) => (
            <div key={s.label} className="border border-white/10 bg-[#111111] p-5">
              <s.icon className="w-5 h-5 text-[#FF7900] mb-3" />
              <p className="font-display text-3xl font-medium">{s.value}</p>
              <p className="text-xs uppercase tracking-widest text-white/40 mt-1">{s.label}</p>
            </div>
          ))}
        </motion.div>

        <ConversionChart />

        <TopSimulations />

        <WinRates />

        <RevenueTracker />

        <Appointments />

        <PendingReviews />

        <ReviewShare />
        <ReviewQRCard />
        <BusinessCard />
        <GoogleRating />
        <TrafficPanel />
        <RegionStats />
        <ApolloPanel />
        <PulsePanel />

        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap" data-testid="dashboard-tabs">
            {["leads", "offers", "referrals", "reviews", "blog", "prospection"].map((k) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                data-testid={`dashboard-tab-${k}`}
                className={`px-5 py-2 rounded-full text-sm transition-colors duration-300 ${
                  tab === k ? "bg-[#FF7900] text-white" : "border border-white/15 text-white/60 hover:text-white"
                }`}
              >
                {d.tabs[k]}
              </button>
            ))}
          </div>
          {tab === "leads" && (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-white/30 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={d.search}
                  data-testid="leads-search-input"
                  className="bg-black/40 border border-white/15 pl-9 pr-4 py-2 rounded-full text-xs w-56 outline-none focus:border-[#FF7900] transition-colors duration-300 placeholder:text-white/25"
                />
              </div>
              <button
                onClick={() => setOldestFirst(!oldestFirst)}
                data-testid="leads-sort-toggle"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs transition-colors duration-300 ${
                  oldestFirst ? "bg-amber-500/15 text-amber-400 border border-amber-500/40" : "border border-white/15 text-white/50 hover:text-white"
                }`}
              >
                <Clock className="w-3.5 h-3.5" />
                {oldestFirst ? d.sort.oldest : d.sort.newest}
              </button>
            </div>
          )}
        </div>

        {tab === "offers" ? (
          <>
            <OffersPanel />
            <div className="mt-8">
              <OfferHistory />
            </div>
          </>
        ) : tab === "referrals" ? (
          <>
            <ReferralsPanel />
            <ReferralPost />
          </>
        ) : tab === "reviews" ? (
          <ReviewsPanel />
        ) : tab === "blog" ? (
          <BlogPanel />
        ) : tab === "prospection" ? (
          <BrusselsProspector />
        ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="border border-white/10 bg-[#111111] overflow-x-auto"
        >
          <table className="w-full text-sm" data-testid="leads-table">
            <thead>
              <tr className="border-b border-white/10 text-left text-xs uppercase tracking-widest text-white/40">
                <th className="px-5 py-4 font-medium">{d.cols.client}</th>
                <th className="px-5 py-4 font-medium">{d.cols.contact}</th>
                <th className="px-5 py-4 font-medium">{d.cols.interest}</th>
                <th className="px-5 py-4 font-medium">{d.cols.score}</th>
                <th className="px-5 py-4 font-medium min-w-[260px]">{d.cols.summary}</th>
                <th className="px-5 py-4 font-medium">{d.cols.status}</th>
                <th className="px-5 py-4 font-medium">{d.cols.actions}</th>
              </tr>
            </thead>
            <tbody>
              {leads && leads.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center text-white/40" data-testid="leads-empty">
                    {d.empty}
                  </td>
                </tr>
              )}
              {sortedLeads &&
                sortedLeads.map((l) => (
                  <Fragment key={l.id}>
                  <tr className={`border-b border-white/5 hover:bg-white/[0.02] transition-colors duration-200 ${isSnoozed(l) ? "opacity-50" : ""}`} data-testid={`lead-row-${l.id}`}>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-white">
                        {l.name}
                        {l.replied_via_sequence && (
                          <span className="ml-2 inline-flex items-center gap-1 align-middle text-[10px] uppercase tracking-wider bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full" data-testid={`lead-hot-badge-${l.id}`}>
                            <Flame className="w-3 h-3" />
                            {d.hotReply}
                          </span>
                        )}
                        {l.source === "chat" && (
                          <span className="ml-2 inline-block align-middle text-[10px] uppercase tracking-wider bg-[#FF7900]/15 text-[#FF7900] px-2 py-0.5 rounded-full" data-testid={`lead-chat-badge-${l.id}`}>
                            {d.viaChat}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-white/40">{l.city}</p>
                      {l.referred_by && (
                        <p className="text-xs text-[#FF7900]/80 mt-0.5" data-testid={`lead-referred-by-${l.id}`}>
                          {d.referredBy}: {l.referred_by}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <p className="text-white/70">{l.email}</p>
                      <p className="text-xs text-white/40">{l.phone}</p>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-block border border-white/15 px-3 py-1 rounded-full text-xs text-white/70">
                        {t.form.options[l.interest] || l.interest}
                      </span>
                      {l.simulated_pack && (
                        <p className="text-[11px] text-[#FF7900]/80 mt-1.5 max-w-[180px] leading-snug" data-testid={`lead-sim-${l.id}`}>
                          {l.simulated_pack}
                        </p>
                      )}
                      {l.bill_path && (
                        <button
                          onClick={() => openBill(l)}
                          data-testid={`lead-bill-${l.id}`}
                          className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] text-emerald-400 hover:text-emerald-300 border border-emerald-500/30 px-2.5 py-1 rounded-full transition-colors duration-200"
                        >
                          <Paperclip className="w-3 h-3" />
                          {d.bill}
                        </button>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-block border px-3 py-1 rounded-full text-xs font-semibold ${scoreStyle[l.ai_score] || scoreStyle.pending}`}
                        data-testid={`lead-score-${l.id}`}
                      >
                        {d.scores[l.ai_score] || d.scores.pending}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-white/60 text-xs leading-relaxed">
                      {l.ai_score === "pending" ? (
                        <span className="text-white/30">{d.pending}</span>
                      ) : (
                        <>
                          <p>{l.ai_summary}</p>
                          <p className="mt-1 text-[#FF7900]/80">
                            {d.nextAction}: {l.ai_next_action}
                          </p>
                        </>
                      )}
                      {l.bill_analysis && (
                        <div className="mt-2 border border-emerald-500/25 bg-emerald-500/5 px-3 py-2" data-testid={`lead-bill-analysis-${l.id}`}>
                          <p className="text-emerald-400 font-semibold text-[11px] uppercase tracking-wider">
                            {d.billAI.title}
                            {l.bill_analysis.monthly_savings != null && ` · ${d.billAI.savings} €${l.bill_analysis.monthly_savings}${d.billAI.perMonth}`}
                          </p>
                          {l.bill_analysis.current_monthly_cost != null && (
                            <p className="mt-1 text-white/70">
                              {l.bill_analysis.supplier ? `${l.bill_analysis.supplier} · ` : ""}€{l.bill_analysis.current_monthly_cost}{d.billAI.perMonth} → Eneco ≈ €{l.bill_analysis.eneco_monthly_estimate ?? "?"}{d.billAI.perMonth}
                            </p>
                          )}
                          {l.bill_analysis.summary && <p className="mt-1 text-white/40">{l.bill_analysis.summary}</p>}
                        </div>
                      )}
                      {l.bill_path && !l.bill_analysis && (
                        <button
                          onClick={() => analyzeBill(l.id)}
                          disabled={analyzing === l.id}
                          data-testid={`lead-analyze-bill-${l.id}`}
                          className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-emerald-400 hover:text-emerald-300 disabled:opacity-50 border border-emerald-500/30 px-2.5 py-1 rounded-full transition-colors duration-200"
                        >
                          <Sparkles className="w-3 h-3" />
                          {analyzing === l.id ? d.billAI.analyzing : d.billAI.analyze}
                        </button>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <select
                        value={l.status}
                        onChange={(e) => updateStatus(l.id, e.target.value)}
                        data-testid={`lead-status-${l.id}`}
                        className={`bg-transparent border border-white/15 px-3 py-1.5 rounded-full text-xs cursor-pointer outline-none ${statusStyle[l.status]}`}
                      >
                        {Object.entries(d.statuses).map(([k, v]) => (
                          <option key={k} value={k} className="bg-[#111] text-white">
                            {v}
                          </option>
                        ))}
                      </select>
                      {l.status === "converted" && (
                        <div className="mt-2 flex items-center gap-1.5" title={d.value.title}>
                          <span className="text-xs text-emerald-400 font-semibold">€</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            defaultValue={l.monthly_value ?? ""}
                            placeholder="0"
                            onBlur={(e) => saveValue(l.id, e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                            data-testid={`lead-value-input-${l.id}`}
                            className="w-20 bg-black/40 border border-white/15 px-2 py-1 text-xs outline-none focus:border-emerald-400 transition-colors duration-300"
                          />
                          <span className="text-[10px] text-white/30">{d.value.suffix}</span>
                        </div>
                      )}
                      {isSnoozed(l) ? (
                        <span
                          className="mt-2 flex items-center gap-1.5 w-fit border border-sky-500/40 bg-sky-500/10 text-sky-400 px-2.5 py-0.5 rounded-full text-[11px] font-medium"
                          data-testid={`lead-snoozed-${l.id}`}
                        >
                          <AlarmClock className="w-3 h-3" />
                          {d.snooze.until} {l.snoozed_until}
                        </span>
                      ) : snoozeExpired(l) && (l.status === "new" || l.status === "contacted") ? (
                        <span
                          className="mt-2 flex items-center gap-1.5 w-fit border border-red-500/40 bg-red-500/10 text-red-400 px-2.5 py-0.5 rounded-full text-[11px] font-semibold"
                          data-testid={`lead-snooze-expired-${l.id}`}
                        >
                          <AlarmClock className="w-3 h-3" />
                          {d.snooze.expired}
                        </span>
                      ) : (l.status === "new" || l.status === "contacted") && (
                        <span
                          className={`mt-2 flex items-center gap-1.5 w-fit border px-2.5 py-0.5 rounded-full text-[11px] font-medium ${waitStyle(waitDays(l.created_at))}`}
                          data-testid={`lead-waiting-${l.id}`}
                        >
                          <Clock className="w-3 h-3" />
                          {waitDays(l.created_at) === 0 ? d.waiting.today : `${waitDays(l.created_at)} ${d.waiting.days}`}
                        </span>
                      )}
                      {l.contract_end && Math.ceil((new Date(l.contract_end) - Date.now()) / 86400000) <= 60 && Math.ceil((new Date(l.contract_end) - Date.now()) / 86400000) >= 0 && (
                        <span
                          className="mt-2 flex items-center gap-1.5 w-fit border border-amber-500/40 bg-amber-500/10 text-amber-400 px-2.5 py-0.5 rounded-full text-[11px] font-semibold"
                          data-testid={`lead-renewal-${l.id}`}
                        >
                          <CalendarDays className="w-3 h-3" />
                          {d.renewal.badge} {l.contract_end}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => downloadBrief(l)}
                          data-testid={`lead-pdf-${l.id}`}
                          aria-label={d.pdfBrief}
                          title={d.pdfBrief}
                          className="text-white/30 hover:text-[#FF7900] transition-colors duration-300"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => downloadProposal(l)}
                          data-testid={`lead-proposal-${l.id}`}
                          aria-label={d.proposalPdf}
                          title={d.proposalPdf}
                          className="text-white/30 hover:text-emerald-400 transition-colors duration-300"
                        >
                          <BadgePercent className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => sendProposal(l)}
                          disabled={sendingProposal === l.id}
                          data-testid={`lead-send-proposal-${l.id}`}
                          aria-label={d.sendProposal}
                          title={l.proposal_sent_at ? `${d.proposalSentOn} ${l.proposal_sent_at.slice(0, 10)}` : d.sendProposal}
                          className={`transition-colors duration-300 disabled:opacity-40 ${l.proposal_sent_at ? "text-emerald-400 hover:text-emerald-300" : "text-white/30 hover:text-emerald-400"}`}
                        >
                          <Send className={`w-4 h-4 ${sendingProposal === l.id ? "animate-pulse" : ""}`} />
                        </button>
                        <button
                          onClick={() => copyPortal(l)}
                          data-testid={`lead-portal-${l.id}`}
                          aria-label={d.portalLink}
                          title={d.portalLink}
                          className="text-white/30 hover:text-[#FF7900] transition-colors duration-300"
                        >
                          <Link2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => copyReviewLink(l)}
                          data-testid={`lead-review-link-${l.id}`}
                          aria-label={d.reviewLink}
                          title={d.reviewLink}
                          className="text-white/30 hover:text-[#FBBC04] transition-colors duration-300"
                        >
                          <Star className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => copyReviewSms(l)}
                          data-testid={`lead-review-sms-${l.id}`}
                          aria-label={d.reviewSms}
                          title={d.reviewSms}
                          className="text-white/30 hover:text-[#FBBC04] transition-colors duration-300"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setOpenNotes(openNotes === l.id ? null : l.id);
                            setNoteText("");
                          }}
                          data-testid={`lead-notes-toggle-${l.id}`}
                          aria-label={d.notes.title}
                          className={`relative transition-colors duration-300 ${openNotes === l.id ? "text-[#FF7900]" : "text-white/30 hover:text-[#FF7900]"}`}
                        >
                          <MessageSquare className="w-4 h-4" />
                          {(l.notes || []).length > 0 && (
                            <span className="absolute -top-2 -right-2 text-[9px] bg-[#FF7900] text-white rounded-full w-3.5 h-3.5 flex items-center justify-center">
                              {(l.notes || []).length}
                            </span>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setOpenSnooze(openSnooze === l.id ? null : l.id);
                            setSnoozeDate(l.snoozed_until || "");
                          }}
                          data-testid={`lead-snooze-toggle-${l.id}`}
                          aria-label={d.snooze.title}
                          className={`transition-colors duration-300 ${
                            isSnoozed(l) ? "text-sky-400" : openSnooze === l.id ? "text-[#FF7900]" : "text-white/30 hover:text-sky-400"
                          }`}
                        >
                          <AlarmClock className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => remove(l.id)}
                          data-testid={`lead-delete-${l.id}`}
                          aria-label={d.delete}
                          className="text-white/30 hover:text-red-400 transition-colors duration-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {openSnooze === l.id && (
                    <tr className="border-b border-white/5 bg-white/[0.015]" data-testid={`lead-snooze-row-${l.id}`}>
                      <td colSpan={7} className="px-5 py-4">
                        <div className="flex items-center gap-3 flex-wrap">
                          <p className="text-xs uppercase tracking-widest text-white/40">{d.snooze.title}</p>
                          <input
                            type="date"
                            value={snoozeDate}
                            min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                            onChange={(e) => setSnoozeDate(e.target.value)}
                            data-testid={`lead-snooze-date-${l.id}`}
                            className="bg-black/40 border border-white/15 px-3 py-1.5 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300 [color-scheme:dark]"
                          />
                          <button
                            onClick={() => snoozeDate && setSnooze(l.id, snoozeDate)}
                            data-testid={`lead-snooze-save-${l.id}`}
                            className="inline-flex items-center gap-2 bg-sky-500/15 border border-sky-500/40 text-sky-400 hover:bg-sky-500 hover:text-white text-xs font-semibold px-4 py-1.5 rounded-full transition-colors duration-300"
                          >
                            <AlarmClock className="w-3.5 h-3.5" /> {d.snooze.save}
                          </button>
                          {l.snoozed_until && (
                            <button
                              onClick={() => setSnooze(l.id, null)}
                              data-testid={`lead-snooze-clear-${l.id}`}
                              className="text-xs text-white/40 hover:text-red-400 transition-colors duration-300"
                            >
                              {d.snooze.clear}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  {openNotes === l.id && (
                    <tr className="border-b border-white/5 bg-white/[0.015]" data-testid={`lead-notes-row-${l.id}`}>
                      <td colSpan={7} className="px-5 py-5">
                        <div className="flex flex-wrap items-end gap-5 mb-5" data-testid={`lead-details-${l.id}`}>
                          <div>
                            <label className="text-[11px] text-white/40 block mb-1.5">{d.details.birthday}</label>
                            <input
                              type="date"
                              defaultValue={l.birthday || ""}
                              onChange={(e) => saveDetail(l.id, "birthday", e.target.value)}
                              data-testid={`lead-birthday-input-${l.id}`}
                              className="bg-black/40 border border-white/15 px-2.5 py-1.5 text-xs outline-none focus:border-[#FF7900] transition-colors duration-300 [color-scheme:dark]"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] text-white/40 block mb-1.5">{d.details.contractEnd}</label>
                            <input
                              type="date"
                              defaultValue={l.contract_end || ""}
                              onChange={(e) => saveDetail(l.id, "contract_end", e.target.value)}
                              data-testid={`lead-contract-input-${l.id}`}
                              className="bg-black/40 border border-white/15 px-2.5 py-1.5 text-xs outline-none focus:border-[#FF7900] transition-colors duration-300 [color-scheme:dark]"
                            />
                          </div>
                        </div>
                        <p className="text-xs uppercase tracking-widest text-white/40 mb-3">{d.notes.title}</p>
                        {(l.notes || []).length === 0 && <p className="text-xs text-white/30 mb-3">{d.notes.empty}</p>}
                        <ul className="flex flex-col gap-2 mb-4">
                          {(l.notes || []).map((n) => (
                            <li key={n.id} className="flex items-start justify-between gap-4 border border-white/10 bg-[#0d0d0d] px-4 py-3" data-testid={`lead-note-${n.id}`}>
                              <div>
                                <p className="text-sm text-white/80">{n.text}</p>
                                <p className="text-[11px] text-white/30 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                              </div>
                              <button
                                onClick={() => removeNote(l.id, n.id)}
                                data-testid={`lead-note-delete-${n.id}`}
                                className="text-white/25 hover:text-red-400 transition-colors duration-300 shrink-0"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                        <div className="flex gap-3">
                          <input
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && addNote(l.id)}
                            placeholder={d.notes.placeholder}
                            data-testid={`lead-note-input-${l.id}`}
                            className="flex-1 bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300"
                          />
                          <button
                            onClick={() => addNote(l.id)}
                            data-testid={`lead-note-add-${l.id}`}
                            className="inline-flex items-center gap-2 bg-[#FF7900] hover:bg-white hover:text-black text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors duration-300"
                          >
                            <Plus className="w-3.5 h-3.5" /> {d.notes.add}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
            </tbody>
          </table>
        </motion.div>
        )}
      </div>
    </main>
  );
}
